import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
    ArrowLeft, Edit2, Trash2, CalendarDays, Users, Camera, CheckCircle2, AlertTriangle,
    ExternalLink, Loader2, ChevronDown, Link2, RotateCcw, ArrowRight, Circle, CircleDot, Mail, Send, Shapes, Tag,
    UserCheck, Video, MapPin, Workflow, X, FileText,
} from 'lucide-react';
import EventPhotosGrid from '../components/events/EventPhotosGrid';
import EventGuestsTab from '../components/events/guests/EventGuestsTab';
import EventActivityTab from '../components/events/EventActivityTab';
import { createEventOutreachApi } from '../lib/eventOutreachApi';
import {
    DISABLED_EVENT_TABS, EVENT_TABS, OUTREACH_LINK_FILTERS, campaignTriggerLabel, canUpdateEvents, countdownLabel, formatClock,
    formatLocalDate, initials, metricValue, modalityLabel, normalizeTabLink, percent, zoneAbbreviation,
} from '../lib/eventWorkspace';
import { errorText } from '../lib/apiErrors';
import { publicFormUrl } from '../lib/formUtils';
import {
    historyActorKind, historyActorName, historyChanges, historyFieldLabel, historyItems, historyKindLabel, historySource,
    historySourceLabel, historyTimestamp, historyValue, humanize,
} from '../lib/historyDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Face review is shown, greyed out and not selectable, until #967's scope is agreed (#1013).
const FACE_REVIEW_PAUSED = 'Face review is paused until its scope is agreed';

const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'guests', label: 'Guests' },
    { key: 'activity', label: 'Activity' },
    { key: 'photos', label: 'Photos' },
    // #1013 (OWN-13): paused until #967's scope is agreed; shown but not selectable.
    { key: 'face_review', label: 'Face review', disabled: true, hint: FACE_REVIEW_PAUSED },
];

const EVENT_STATUS_CONFIG = {
    draft: { label: 'Draft', bg: 'bg-stone-100', text: 'text-stone-600' },
    published: { label: 'Published', bg: 'bg-blue-50', text: 'text-blue-700' },
    // Values stored before the Draft/Published/Completed/Cancelled vocabulary (#898).
    scheduled: { label: 'Published', bg: 'bg-blue-50', text: 'text-blue-700' },
    in_progress: { label: 'Published', bg: 'bg-blue-50', text: 'text-blue-700' },
    upcoming: { label: 'Upcoming', bg: 'bg-blue-50', text: 'text-blue-700' },
    active: { label: 'Active', bg: 'bg-emerald-50', text: 'text-emerald-700' },
    completed: { label: 'Completed', bg: 'bg-stone-100', text: 'text-stone-700' },
    cancelled: { label: 'Cancelled', bg: 'bg-red-50', text: 'text-red-600' },
};

// User-selectable statuses shown in the badge dropdown. `upcoming` and
// `active` exist for backwards-compat rendering but aren't offered as
// manual choices.
const EVENT_STATUS_OPTIONS = ['draft', 'published', 'completed', 'cancelled'];

const formatDuration = (minutes) => {
    if (!minutes) return '--';
    if (minutes < 60) return `${minutes}m`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
};


// Code-split the editor — it only loads when the user opens the Edit dialog.
const LazyEventEditor = React.lazy(() => import('./EventEditor'));

/* ========== MAIN COMPONENT ========== */

export default function EventDetail() {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, getAuthHeader } = useAuth();
    const { hasEffectivePermission } = usePermissions();
    const canContributeActivity = hasEffectivePermission('activities:create');
    // Start / End / Reopen / Reschedule, the event's own edits and its campaigns all need events:update (D9).
    const canUpdateEvent = canUpdateEvents(hasEffectivePermission);
    const readOnlyReason = 'You do not have permission to add activities.';

    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    // The tab lives in the URL (?tab=…), so every deep link and every refresh
    // restores it (#898). Guest filters ride along (?owner_id=…, ?column=…).
    // #1013: the pre-Guests keys (outreach, attendees, follow_up) open Guests, and a paused tab (Face review) opens
    // Overview: it is listed but can never be the active tab.
    const rawTab = normalizeTabLink(searchParams.get('tab')).tab;
    const requestedTab = !EVENT_TABS.includes(rawTab) ? null : (DISABLED_EVENT_TABS.includes(rawTab) ? 'overview' : rawTab);
    const guestFilters = useMemo(() => normalizeTabLink('guests', Object.fromEntries(
        OUTREACH_LINK_FILTERS.filter(key => searchParams.get(key)).map(key => [key, searchParams.get(key)]),
    )).filters, [searchParams]);
    // ?contact= (Contact Detail's Events tab, #889, and the Activity tab's guest names) names the contact whose
    // drawer Guests opens: read once, then cleared from the URL like ?edit=1.
    const [linkedContactId, setLinkedContactId] = useState(() => searchParams.get('contact') || '');
    const [overview, setOverview] = useState(null);
    const [overviewLoading, setOverviewLoading] = useState(true);
    const [overviewError, setOverviewError] = useState(null);
    const [recentHistory, setRecentHistory] = useState({ items: [], loading: true, error: false });

    // Dialogs
    const [showEdit, setShowEdit] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [showStatusMenu, setShowStatusMenu] = useState(false);
    const [updatingEventStatus, setUpdatingEventStatus] = useState(false);

    // A deleted event is still served (#887 P8), but every guest write on it answers 404: say so, lock them
    // and the event's own Edit, Delete and status controls, and offer Restore instead.
    const eventDeleted = Boolean(event?.is_deleted);

    /* ---------- Load Event ---------- */
    const loadEvent = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/events/${eventId}`, { headers: getAuthHeader() });
            setEvent(res.data);
        } catch {
            toast.error('Failed to load event');
        } finally {
            setLoading(false);
        }
    }, [eventId, getAuthHeader]);

    const outreachApi = useMemo(() => createEventOutreachApi(getAuthHeader), [getAuthHeader]);
    const loadOverview = useCallback(async () => {
        setOverviewLoading(true);
        setOverviewError(null);
        try {
            // "everyone" asks for every row the caller may see, like the Guests board's
            // default; the server clamps an assigned viewer to their own contacts.
            setOverview(await outreachApi.getOverview(eventId, { scope: 'everyone' }));
        } catch (err) {
            setOverview(null);
            const denied = err?.response?.status === 403;
            setOverviewError({ denied, message: denied ? 'Your role cannot read this event’s outreach.' : errorText(err, 'Something went wrong on our side. Nothing you saved has been lost.') });
        } finally {
            setOverviewLoading(false);
        }
    }, [eventId, outreachApi]);

    const loadRecentHistory = useCallback(async () => {
        setRecentHistory(current => ({ ...current, loading: true, error: false }));
        try {
            // The visibility-scoped event history route (#890/#891, journal-backed since #1013); five rows only.
            const response = await axios.get(`${API}/events/${eventId}/history`, { headers: getAuthHeader(), params: { limit: 5 } });
            setRecentHistory({ items: historyItems(response.data).slice(0, 5), loading: false, error: false });
        } catch {
            setRecentHistory({ items: [], loading: false, error: true });
        }
    }, [eventId, getAuthHeader]);

    const linkCampaign = async (campaignId) => {
        if (!campaignId) return;
        try {
            await axios.post(`${API}/events/${eventId}/link-campaign/${encodeURIComponent(campaignId)}`, {}, { headers: getAuthHeader() });
            toast.success('Campaign linked');
            await loadEvent();
        } catch (err) {
            toast.error(errorText(err, 'Could not link the campaign'));
        }
    };
    const unlinkCampaign = async (campaignId) => {
        try {
            await axios.delete(`${API}/events/${eventId}/link-campaign/${encodeURIComponent(campaignId)}`, { headers: getAuthHeader() });
            toast.success('Campaign unlinked');
            await loadEvent();
        } catch (err) {
            toast.error(errorText(err, 'Could not unlink the campaign'));
        }
    };

    useEffect(() => { loadEvent(); }, [loadEvent]);
    useEffect(() => { loadOverview(); }, [loadOverview]);
    useEffect(() => { loadRecentHistory(); }, [loadRecentHistory]);

    // The Guests tab changed the event (Start, End, Reopen, Reschedule): re-read what this page shows from it.
    const onGuestsChanged = useCallback(() => {
        loadEvent();
        loadOverview();
        loadRecentHistory();
    }, [loadEvent, loadOverview, loadRecentHistory]);

    // With no ?tab=, an event before its date opens on Guests (the working screen) and any other on Overview.
    // An explicit tab (a click or a deep link) always wins. The server decides `phase` from the event's own
    // calendar and its Start / End times (#896, #1013).
    const activeTab = requestedTab || (event?.phase === 'before' ? 'guests' : 'overview');
    // Coming back to Overview re-reads it, so its counts (photos included, #900) reflect what
    // was just done on another tab instead of the numbers from when the page opened.
    const lastTabRef = useRef(activeTab);
    useEffect(() => {
        if (activeTab === 'overview' && lastTabRef.current !== 'overview') loadOverview();
        lastTabRef.current = activeTab;
    }, [activeTab, loadOverview]);
    const openTab = useCallback((tab, filters = {}) => {
        const link = normalizeTabLink(tab, filters);
        const next = new URLSearchParams();
        if (link.tab) next.set('tab', link.tab);
        OUTREACH_LINK_FILTERS.forEach(key => { if (link.filters[key]) next.set(key, String(link.filters[key])); });
        setSearchParams(next, { replace: true });
        window.scrollTo?.(0, 0);
    }, [setSearchParams]);
    // The Activity tab's guest names open that guest's drawer on Guests: the same ?contact= link Contact Detail uses.
    const openGuest = useCallback((contactId) => {
        if (!contactId) return;
        setSearchParams(new URLSearchParams({ tab: 'guests', contact: String(contactId) }), { replace: true });
        window.scrollTo?.(0, 0);
    }, [setSearchParams]);

    // ?edit=1 (an Events card's Edit, or a deep link) opens the editor once.
    useEffect(() => {
        if (!event || searchParams.get('edit') !== '1') return;
        if (!event.is_deleted) setShowEdit(true);
        const next = new URLSearchParams(searchParams);
        next.delete('edit');
        setSearchParams(next, { replace: true });
    }, [event, searchParams, setSearchParams]);

    useEffect(() => {
        const linked = searchParams.get('contact');
        if (!linked) return;
        setLinkedContactId(linked);
        const next = new URLSearchParams(searchParams);
        next.delete('contact');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);
    // Only the Guests tab can open it: on another tab the link is dropped, so a later visit to
    // Guests does not open a drawer nobody asked for.
    useEffect(() => {
        if (event && activeTab !== 'guests') setLinkedContactId('');
    }, [event, activeTab]);

    /* ---------- Change Event Status ---------- */
    const handleEventStatusChange = async (newStatus) => {
        if (newStatus === event?.status) { setShowStatusMenu(false); return; }
        setUpdatingEventStatus(true);
        try {
            await axios.put(
                `${API}/events/${eventId}`,
                { status: newStatus },
                { headers: getAuthHeader() },
            );
            setEvent(prev => ({ ...prev, status: newStatus }));
            toast.success(`Status → ${EVENT_STATUS_CONFIG[newStatus]?.label || newStatus}`);
            loadOverview();
            loadRecentHistory();
        } catch (err) {
            toast.error(errorText(err, 'Failed to update status'));
        } finally {
            setUpdatingEventStatus(false);
            setShowStatusMenu(false);
        }
    };

    /* ---------- Delete Event ---------- */
    const handleDeleteEvent = async () => {
        setDeleting(true);
        try {
            await axios.delete(`${API}/events/${eventId}`, { headers: getAuthHeader() });
            toast.success('Event deleted');
            navigate('/events');
        } catch {
            toast.error('Failed to delete event');
        } finally {
            setDeleting(false);
        }
    };

    /* ---------- Restore Event ---------- */
    // Undoes the soft delete. The button stays disabled until the reload shows the live event, so the banner
    // never offers Restore again in between.
    const handleRestoreEvent = async () => {
        setRestoring(true);
        try {
            await axios.post(`${API}/events/${eventId}/restore`, {}, { headers: getAuthHeader() });
            toast.success('Event restored');
            await loadEvent();
        } catch (err) {
            toast.error(errorText(err, 'Failed to restore event'));
        } finally {
            setRestoring(false);
        }
    };

    /* ---------- Loading / Error ---------- */
    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!event) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
                <AlertTriangle className="w-8 h-8 text-stone-400" />
                <p className="text-stone-500">Event not found</p>
                <Button variant="outline" size="sm" onClick={() => navigate('/events')}>
                    <ArrowLeft className="w-4 h-4 mr-1" /> Back to Events
                </Button>
            </div>
        );
    }

    const eventStatus = EVENT_STATUS_CONFIG[event.status] || EVENT_STATUS_CONFIG.draft;

    /* ========== EDIT MODE — inline on the page ========== */
    if (showEdit) {
        return (
            <div className="space-y-4 max-w-5xl animate-fade-in">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowEdit(false)}
                        className="text-stone-500 hover:text-stone-800 p-1"
                        aria-label="Back to event"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <h1 className="text-lg font-semibold text-stone-800">Edit event</h1>
                </div>
                <React.Suspense fallback={
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
                    </div>
                }>
                    <LazyEventEditor
                        eventId={eventId}
                        onCancel={() => setShowEdit(false)}
                        onSave={() => {
                            setShowEdit(false);
                            loadEvent();
                        }}
                    />
                </React.Suspense>
            </div>
        );
    }

    /* ========== RENDER ========== */
    return (
        <div className="space-y-6 w-full min-w-0 animate-fade-in">
            {/* ---- Header ---- */}
            <div className="flex items-start gap-4">
                <button
                    onClick={() => navigate('/events')}
                    className="mt-1 p-2 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2.5">
                                <h1 className="text-2xl font-bold text-stone-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {event.name || 'Untitled Event'}
                                </h1>
                                {event.event_number && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-semibold bg-stone-100 text-stone-500 border border-stone-200 shrink-0">
                                        {event.event_number}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2 mt-1.5">
                                {/* Click-to-change status. Admins + event owner
                                    can switch between Draft / Published /
                                    Completed / Cancelled. Locked on a deleted event. */}
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => { if (!eventDeleted) setShowStatusMenu(v => !v); }}
                                        disabled={updatingEventStatus || eventDeleted}
                                        data-action="event.change_status"
                                        title={eventDeleted ? 'Restore the event to change its status' : 'Change status'}
                                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${eventStatus.bg} ${eventStatus.text} hover:ring-2 hover:ring-stone-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:ring-0`}
                                    >
                                        {updatingEventStatus ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                                        {eventStatus.label}
                                        <ChevronDown className="w-3 h-3" />
                                    </button>
                                    {showStatusMenu && !eventDeleted && (
                                        <div
                                            className="absolute left-0 top-full mt-1 w-40 bg-white border border-stone-200 rounded-lg shadow-lg z-20 py-1"
                                            onMouseLeave={() => setShowStatusMenu(false)}
                                        >
                                            {EVENT_STATUS_OPTIONS.map(s => {
                                                const cfg = EVENT_STATUS_CONFIG[s];
                                                const active = event.status === s;
                                                return (
                                                    <button
                                                        key={s}
                                                        type="button"
                                                        onClick={() => handleEventStatusChange(s)}
                                                        className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between ${
                                                            active ? 'bg-stone-50' : 'hover:bg-stone-50'
                                                        }`}
                                                    >
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>
                                                            {cfg.label}
                                                        </span>
                                                        {active && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                {event.event_type && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-stone-50 text-stone-500 border border-stone-200">
                                        {event.event_type}
                                    </span>
                                )}
                            </div>
                        </div>
                        {/* `Button` sets `disabled:pointer-events-none`, which would swallow the hover that shows
                            the title: let it through, so a deleted event says why Edit and Delete are off. */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { if (!eventDeleted) setShowEdit(true); }}
                                disabled={eventDeleted}
                                title={eventDeleted ? 'Restore the event to edit it' : undefined}
                                data-action="event.edit"
                                className="disabled:pointer-events-auto disabled:cursor-not-allowed"
                            >
                                <Edit2 className="w-4 h-4 mr-1" /> Edit
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { if (!eventDeleted) setShowDeleteConfirm(true); }}
                                disabled={eventDeleted}
                                title={eventDeleted ? 'This event is already deleted' : 'Delete event'}
                                aria-label="Delete event"
                                data-action="event.delete"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 disabled:pointer-events-auto disabled:cursor-not-allowed"
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {eventDeleted && (
                <div
                    role="status"
                    data-testid="event-deleted-banner"
                    className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                >
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1 min-w-0">
                        This event was deleted. Restore it to change its details or attendees.
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRestoreEvent}
                        disabled={restoring}
                        data-action="event.restore"
                        className="flex-shrink-0 bg-white border-amber-300 text-amber-900 hover:bg-amber-100"
                    >
                        {restoring ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}
                        Restore event
                    </Button>
                </div>
            )}

            {/* ---- Tabs ---- */}
            <div className="border-b border-stone-200">
                <nav className="flex gap-6 -mb-px overflow-x-auto" role="tablist" aria-label="Event sections">
                    {TABS.map(tab => {
                        const paused = Boolean(tab.disabled);
                        const selected = !paused && activeTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                role="tab"
                                aria-selected={selected}
                                aria-disabled={paused ? 'true' : undefined}
                                disabled={paused || undefined}
                                title={paused ? tab.hint : undefined}
                                data-testid={`event-tab-${tab.key}`}
                                onClick={() => (paused ? toast.info(tab.hint) : openTab(tab.key))}
                                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                                    paused
                                        ? 'border-transparent text-stone-400 opacity-[.45] cursor-not-allowed'
                                        : selected
                                            ? 'border-stone-900 text-stone-900'
                                            : 'border-transparent text-stone-400 hover:text-stone-600 hover:border-stone-300'
                                }`}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* ---- Tab Content ---- */}
            {activeTab === 'overview' && (
                <OverviewTab
                    event={event}
                    overview={overview}
                    loading={overviewLoading}
                    error={overviewError}
                    onRetry={loadOverview}
                    history={recentHistory}
                    onRetryHistory={loadRecentHistory}
                    onOpenTab={openTab}
                    onEdit={() => { if (!eventDeleted) setShowEdit(true); }}
                    onPublish={() => handleEventStatusChange('published')}
                    publishing={updatingEventStatus}
                    isAdmin={Boolean(user?.is_admin)}
                    canManageCampaigns={canUpdateEvent}
                    getAuthHeader={getAuthHeader}
                    onLinkCampaign={linkCampaign}
                    onUnlinkCampaign={unlinkCampaign}
                    navigate={navigate}
                    readOnly={eventDeleted}
                />
            )}
            {activeTab === 'guests' && (
                <EventGuestsTab
                    eventId={eventId}
                    event={event}
                    getAuthHeader={getAuthHeader}
                    readOnly={eventDeleted || !canContributeActivity}
                    canManageLifecycle={canUpdateEvent && !eventDeleted}
                    initialFilters={guestFilters}
                    initialContactId={linkedContactId || undefined}
                    onEventChanged={onGuestsChanged}
                />
            )}
            {activeTab === 'activity' && (
                <EventActivityTab eventId={eventId} getAuthHeader={getAuthHeader} timeZone={event?.timezone} onOpenGuest={openGuest} />
            )}
            {/* Face review is paused (#1013, OWN-13), so the photo viewer offers no Review link into it. */}
            {activeTab === 'photos' && <EventPhotosGrid eventId={eventId} readOnlyReason={!canContributeActivity ? readOnlyReason : null} />}

            {/* ---- Delete Confirmation Dialog ---- */}
            <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <DialogContent style={{ width: '420px', maxWidth: '95vw', maxHeight: '80vh' }}>
                    <DialogHeader>
                        <DialogTitle>Delete Event</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-stone-600">
                            Are you sure you want to delete <span className="font-semibold">{event.name}</span>? It will be
                            hidden from your events and its attendees can no longer be changed. The attendee records and
                            their history are kept.
                        </p>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
                            <Button
                                size="sm"
                                onClick={handleDeleteEvent}
                                disabled={deleting}
                                className="bg-red-600 hover:bg-red-700 text-white"
                            >
                                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                                Delete Event
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

/* ========== OVERVIEW TAB (#899) ========== */

const NEXT_STEP_COPY = {
    publish: { icon: Send, tone: 'stone', text: () => 'Publish to start outreach', sub: () => 'A draft event has no outreach, RSVPs or reminders yet.', button: 'Publish' },
    invite: {
        icon: Mail, tone: 'blue',
        text: (step) => `${step.count} contact${step.count === 1 ? '' : 's'} not yet invited`,
        sub: (step) => (step.overdue ? `Plus ${step.overdue} follow-up${step.overdue === 1 ? '' : 's'} overdue.` : 'Invite them from the Guests board.'),
        button: 'Open guests',
    },
    follow_ups_overdue: {
        icon: AlertTriangle, tone: 'red',
        text: (step) => `${step.count} follow-up${step.count === 1 ? '' : 's'} overdue`,
        sub: () => 'Their due date has passed and nobody has completed them.',
        button: 'Open guests',
    },
    review_rsvps: {
        icon: CalendarDays, tone: 'blue',
        text: (step, overview) => `${step.count} confirmed${overview.event?.capacity != null ? ` of ${overview.event.capacity} seats` : ''}`,
        sub: (step) => (step.days_until != null ? `Everyone is invited. The event is ${countdownLabel(step.days_until).toLowerCase()}.` : 'Everyone is invited.'),
        button: 'Review guests',
    },
    check_in: { icon: UserCheck, tone: 'stone', text: (step) => `Event day · ${step.count} confirmed`, sub: () => 'Start the event, then check people in as they arrive.', button: 'Open check-in' },
    mark_attendance: {
        icon: UserCheck, tone: 'amber',
        text: (step) => `${step.count} attendee${step.count === 1 ? '' : 's'} unmarked`,
        sub: () => 'End the event to move Going guests without a check-in to No-show.',
        button: 'Open check-in',
    },
    view_results: {
        icon: CheckCircle2, tone: 'emerald',
        text: () => 'Follow-up is up to date',
        sub: (step, overview) => `${overview.metrics.attended} attended · ${metricValue(overview.metrics, 'meetings_booked')} meetings booked · ${metricValue(overview.metrics, 'converted')} converted`,
        button: 'View guests',
    },
};

const TONE = {
    stone: 'bg-stone-100 text-stone-700 border-stone-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
};

const card = 'overflow-hidden rounded-[10px] border border-stone-200 bg-white';
const cardHead = 'flex items-center gap-2.5 border-b border-stone-100 px-4 py-3';

function addDays(localDate, days) {
    if (!localDate) return null;
    const date = new Date(`${localDate}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
}

/** Before · outreach / Event day / After · follow-up, read in the event's timezone (server `timeline`). */
function PhaseTimeline({ overview }) {
    const { timeline, metrics, event } = overview;
    const current = timeline.current;
    const order = ['before', 'event_day', 'after'];
    const index = order.indexOf(current);
    const day = formatLocalDate(timeline.local_date);
    const time = timeline.start_time ? ` · ${formatClock(timeline.start_time)}` : '';
    const zone = zoneAbbreviation(timeline.timezone, timeline.local_date);
    const momentState = (phaseIndex, value) => {
        if (phaseIndex < index) return 'done';
        if (phaseIndex > index) return 'future';
        return value;
    };
    const phases = [
        {
            key: 'before', label: 'Before · outreach',
            range: timeline.local_date ? `until ${formatLocalDate(addDays(timeline.local_date, -1))}` : 'No date set',
            fill: index > 0 ? 100 : percent(metrics.invited, metrics.on_list),
            moments: [
                { label: 'Invited', value: `${metrics.invited} of ${metrics.on_list}`, state: momentState(0, metrics.to_invite ? 'next' : 'done') },
                { label: 'RSVP confirmed', value: `${metrics.rsvp_confirmed}${event.capacity != null ? ` / ${event.capacity}` : ''}`, state: momentState(0, 'next') },
            ],
        },
        {
            key: 'event_day', label: 'Event day', range: timeline.local_date ? `${day}${time}` : '—',
            fill: index > 1 ? 100 : index === 1 ? percent(metrics.attended, metrics.rsvp_confirmed) : 0,
            moments: [
                { label: `Event${time}`, value: zone ? `${day} (${zone})` : day, state: momentState(1, 'next') },
                { label: 'Checked in', value: index >= 1 ? String(metrics.attended) : '—', state: momentState(1, metrics.unmarked ? 'next' : 'done') },
            ],
        },
        {
            key: 'after', label: 'After · follow-up',
            range: timeline.local_date ? `${formatLocalDate(addDays(timeline.end_local_date || timeline.local_date, 1))} →` : '—',
            fill: index === 2 ? percent(metrics.on_list - metrics.follow_ups_open, metrics.on_list) : 0,
            moments: [
                { label: 'Follow-ups overdue', value: index === 2 ? String(metrics.follow_ups_overdue) : '—', state: momentState(2, metrics.follow_ups_overdue ? 'late' : 'done') },
                { label: 'Meetings booked', value: index === 2 ? String(metricValue(metrics, 'meetings_booked')) : '—', state: momentState(2, 'next') },
            ],
        },
    ];
    const momentIcon = { done: CheckCircle2, late: AlertTriangle, next: CircleDot, future: Circle };
    const momentTone = { done: 'text-emerald-500', late: 'text-red-600', next: 'text-blue-700', future: 'text-stone-300' };
    return (
        <div className="grid grid-cols-1 md:grid-cols-3" data-testid="overview-timeline">
            {phases.map((phase, phaseIndex) => {
                const isCurrent = phase.key === current;
                const past = phaseIndex < index;
                return (
                    <div key={phase.key} data-current={isCurrent ? 'true' : undefined} className={`flex min-w-0 flex-col gap-2.5 px-4 py-3.5 ${phaseIndex < 2 ? 'border-b border-stone-100 md:border-b-0 md:border-r' : ''} ${isCurrent ? 'bg-white' : 'bg-stone-50'}`}>
                        <div className="flex items-center gap-2">
                            <span className={`h-2 w-2 flex-none rounded-full ${isCurrent ? 'bg-blue-700' : past ? 'bg-emerald-500' : 'bg-stone-300'}`} />
                            <span className="text-[13px] font-semibold">{phase.label}</span>
                            {isCurrent && <span className="inline-flex h-5 items-center rounded-full bg-stone-900 px-1.5 text-[11px] font-semibold text-white">Now</span>}
                            <span className="flex-1" />
                            <span className="whitespace-nowrap text-xs text-stone-500">{phase.range}</span>
                        </div>
                        <div className="h-1 overflow-hidden rounded bg-stone-200"><div className={`h-full rounded ${past ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{ width: `${Math.min(100, Math.max(0, phase.fill))}%` }} /></div>
                        <div className="flex flex-col gap-1.5">
                            {phase.moments.map((moment) => {
                                const Icon = momentIcon[moment.state];
                                return (
                                    <div key={moment.label} className="flex min-w-0 items-center gap-2 text-[12.5px]">
                                        <Icon className={`h-3.5 w-3.5 flex-none ${momentTone[moment.state]}`} />
                                        <span className={`truncate ${moment.state === 'future' ? 'text-stone-400' : 'text-stone-900'} ${moment.state === 'next' || moment.state === 'late' ? 'font-semibold' : ''}`}>{moment.label}</span>
                                        <span className="flex-1" />
                                        <span className={`whitespace-nowrap tabular-nums ${moment.state === 'late' ? 'font-semibold text-red-700' : 'text-stone-500'}`}>{moment.value}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function NextStep({ overview, onOpenTab, onPublish, publishing }) {
    const step = overview.next_step;
    if (!step) {
        return <div className="flex items-center gap-3 border-t border-stone-100 bg-stone-50 px-4 py-3 text-[13px] text-stone-600" data-testid="overview-next-step">This event was cancelled. Nothing is due.</div>;
    }
    const copy = NEXT_STEP_COPY[step.code] || NEXT_STEP_COPY.review_rsvps;
    const Icon = copy.icon;
    const button = typeof copy.button === 'function' ? copy.button(step) : copy.button;
    return (
        <div className="flex flex-wrap items-center gap-3 border-t border-stone-100 bg-stone-50 px-4 py-3" data-testid="overview-next-step" data-step={step.code}>
            <span className={`flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[9px] border ${TONE[copy.tone]}`}><Icon className="h-4 w-4" /></span>
            <div className="flex min-w-0 flex-[1_1_260px] flex-col gap-px">
                <span className="text-[10.5px] font-semibold tracking-wider text-stone-500">NEXT STEP</span>
                <span className="text-[13.5px] font-semibold">{copy.text(step, overview)}</span>
                <span className="text-[12.5px] text-stone-600">{copy.sub(step, overview)}</span>
            </div>
            <Button
                size="sm"
                disabled={publishing}
                data-testid="overview-next-step-action"
                onClick={() => (step.code === 'publish' ? onPublish() : onOpenTab(step.tab, step.filters))}
            >
                {publishing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}{button}<ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </Button>
        </div>
    );
}

const FUNNEL_SUB = {
    on_list: () => 'contacts',
    invited: (m) => `${percent(m.invited, m.on_list)}% of list`,
    // Contacts can confirm without an outreach invite (registered directly): then the list is the base.
    rsvp_confirmed: (m, event) => `${m.invited ? `${percent(m.rsvp_confirmed, m.invited)}% of invited` : `${percent(m.rsvp_confirmed, m.on_list)}% of list`}${event.capacity != null ? ` · ${event.capacity} seats` : ''}`,
    attended: (m) => `${percent(m.attended, m.rsvp_confirmed)}% turnout${m.walk_ins ? ` · ${m.walk_ins} walk-in${m.walk_ins === 1 ? '' : 's'}` : ''}`,
    // #1013 (P10): meetings_booked / converted replace calls_booked / handed_to_sales; an older API still sends the old keys.
    meetings_booked: (m) => `${percent(metricValue(m, 'meetings_booked'), m.attended)}% of attended`,
    converted: (m) => `${percent(metricValue(m, 'converted'), metricValue(m, 'meetings_booked'))}% of meetings`,
};
FUNNEL_SUB.calls_booked = FUNNEL_SUB.meetings_booked;
FUNNEL_SUB.handed_to_sales = FUNNEL_SUB.converted;

const funnelSub = (key, metrics, event) => FUNNEL_SUB[key]?.(metrics, event) ?? '';

function Funnel({ overview, onOpenTab }) {
    const { funnel, metrics, event, timeline, effective_scope: scope } = overview;
    const note = timeline.current === 'before' ? 'Later steps fill in after the event' : scope?.mine ? 'Your contacts' : 'Whole event · all owners';
    return (
        <section className={card} data-testid="overview-funnel">
            <div className={cardHead}><span className="flex-1 text-[13.5px] font-semibold">Funnel</span><span className="text-xs text-stone-500">{note}</span></div>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))' }}>
                {funnel.map((step, index) => {
                    const off = !step.available;
                    const Element = off ? 'div' : 'button';
                    return (
                        <Element
                            key={step.key}
                            type={off ? undefined : 'button'}
                            onClick={off ? undefined : () => onOpenTab(step.tab)}
                            data-testid={`funnel-${step.key}`}
                            className={`flex min-w-0 flex-col gap-1.5 border-r border-stone-100 px-4 pb-3.5 pt-3 text-left ${off ? 'cursor-default' : 'hover:bg-stone-50'}`}
                        >
                            <span title={step.label} className="truncate text-[10.5px] font-semibold uppercase tracking-wider text-stone-500">{step.label}</span>
                            <span className={`text-xl font-semibold leading-[26px] tabular-nums ${off ? 'text-stone-300' : index >= 2 ? 'text-emerald-700' : 'text-stone-900'}`}>{off ? '—' : step.value}</span>
                            <div className="h-1 overflow-hidden rounded bg-stone-100"><div className={`h-full rounded ${index >= 2 ? 'bg-emerald-500' : 'bg-stone-400'}`} style={{ width: `${off ? 0 : Math.max(3, percent(step.value, metrics.on_list))}%` }} /></div>
                            <span className="truncate text-xs text-stone-500" title={off ? undefined : funnelSub(step.key, metrics, event)}>{off ? 'After the event' : funnelSub(step.key, metrics, event)}</span>
                        </Element>
                    );
                })}
            </div>
        </section>
    );
}

function DetailRow({ icon: Icon, label, children, action }) {
    return (
        <div className="flex items-start gap-3.5 border-b border-stone-100 px-4 py-2.5 last:border-b-0">
            <Icon className="mt-0.5 h-4 w-4 flex-none text-stone-500" />
            <span className="flex-none basis-24 pt-px text-[12.5px] text-stone-500">{label}</span>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-[13px]">{children}</div>
            {action}
        </div>
    );
}

function EventDetailsCard({ event, overview, onEdit, readOnly }) {
    const timeline = overview.timeline;
    const zone = zoneAbbreviation(timeline.timezone, timeline.local_date);
    const time = [event.start_time && formatClock(event.start_time), event.end_time && formatClock(event.end_time)].filter(Boolean).join(' – ');
    const location = event.location && typeof event.location === 'object'
        ? [event.location.venue_name, event.location.address, event.location.city, event.location.state, event.location.country].filter(Boolean)
        : [event.location].filter(Boolean);
    const online = event.location?.online_link || event.online_link;
    const velocity = [['velocity_event_url', 'Event page'], ['velocity_registration_url', 'Registration'], ['velocity_webinar_url', 'Webinar room']]
        .filter(([key]) => event[key]).map(([key, label]) => ({ label, url: event[key] }));
    const links = event.platform === 'velocity' ? velocity : online ? [{ label: event.platform === 'zoom' ? 'Zoom meeting' : 'Meeting link', url: online }] : [];
    const capacity = event.capacity;
    const confirmed = overview.event?.event_totals?.rsvp_confirmed ?? overview.metrics.rsvp_confirmed;
    const tags = (event.tags || []).map((tag) => (typeof tag === 'string' ? tag : tag?.name)).filter(Boolean);
    return (
        <section className={card} data-testid="overview-details">
            <div className={cardHead}>
                <span className="flex-1 text-[13.5px] font-semibold">Event details</span>
                {!readOnly && <Button variant="outline" size="sm" onClick={onEdit}><Edit2 className="mr-1 h-3.5 w-3.5" />Edit</Button>}
            </div>
            <DetailRow icon={CalendarDays} label="Date and time">
                <span className="font-medium tabular-nums">{timeline.local_date ? `${formatLocalDate(timeline.local_date, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}${time ? ` · ${time}` : ''}` : 'Date to be confirmed'}</span>
                {timeline.timezone && <span className="text-[12.5px] text-stone-600">{zone && zone !== timeline.timezone ? `${zone} · ` : ''}{timeline.timezone} time{event.duration_minutes ? ` · ${formatDuration(event.duration_minutes)}` : ''}</span>}
            </DetailRow>
            {event.modality !== 'online' && location.length > 0 && (
                <DetailRow icon={MapPin} label="Venue"><span className="font-medium">{location[0]}</span>{location.length > 1 && <span className="text-[12.5px] text-stone-600">{location.slice(1).join(', ')}</span>}</DetailRow>
            )}
            {links.length > 0 && (
                <DetailRow icon={Video} label="Online">
                    {links.map((link) => (
                        <a key={link.label} href={link.url} target="_blank" rel="noopener noreferrer" className="flex min-w-0 items-center gap-2 text-[12.5px]">
                            <span className="w-28 flex-none text-stone-600">{link.label}</span>
                            <span className="truncate text-stone-900 underline underline-offset-2">{link.url}</span>
                            <ExternalLink className="h-3 w-3 flex-none text-stone-500" />
                        </a>
                    ))}
                </DetailRow>
            )}
            <DetailRow icon={Users} label="Capacity">
                <span className="font-medium tabular-nums">{capacity != null ? `${capacity} seats · ${Math.max(0, capacity - confirmed)} left` : 'No capacity limit'}</span>
            </DetailRow>
            <DetailRow icon={Shapes} label="Type"><span className="font-medium">{[event.event_type, modalityLabel(event.modality, event.platform)].filter(Boolean).join(' · ') || '—'}</span></DetailRow>
            {tags.length > 0 && (
                <DetailRow icon={Tag} label="Tags">
                    <div className="flex flex-wrap gap-1.5">{tags.map((tag) => <span key={tag} className="inline-flex h-[22px] items-center rounded-md border border-stone-200 bg-stone-100 px-2 text-[11.5px] font-medium text-stone-600">{tag}</span>)}</div>
                </DetailRow>
            )}
        </section>
    );
}

function activityTime(item) {
    const raw = item?.changed_at || item?.created_at;
    const date = raw ? new Date(raw) : null;
    if (!date || Number.isNaN(date.getTime())) return historyTimestamp(item);
    return date.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function activityLine(item) {
    // The journal row carries display labels ("Invited" → "Going"); an older row only has raw values.
    if (item?.new_label != null && String(item.new_label) !== '') {
        const from = item.old_label ? `${item.old_label} → ` : '';
        return `${item.field ? `${historyFieldLabel(item.field)} ` : ''}${from}${item.new_label}`;
    }
    const change = historyChanges(item)[0];
    if (change) return `${historyFieldLabel(change.field)} ${historyValue(change.old_value)} → ${historyValue(change.new_value)}`;
    return historyKindLabel(item);
}

// Event-level journal rows (Start, End, Reopen, Reschedule, edits; #1013, OA-1) read as a sentence about the event.
const EVENT_LEVEL_TEXT = {
    'event.started': 'started the event',
    'event.ended': 'ended the event',
    'event.reopened': 'reopened check-in',
    'event.rescheduled': 'rescheduled the event',
    'event.updated': 'edited the event',
    'event.no_show_triggers_dispatched': 'sent the no-show triggers',
};
const isEventLevel = (item) => item?.entity_type === 'event' || String(item?.event_type || '').startsWith('event.');
const eventLevelText = (item) => {
    const base = EVENT_LEVEL_TEXT[item.event_type] || humanize(String(item.event_type || 'event').replace(/^event\./, ''));
    return item.event_type === 'event.rescheduled' && item.new_label ? `${base} to ${item.new_label}` : base;
};
// A system actor is shown as "Automatically", never as an id.
const activityActor = (item) => (historyActorKind(item) === 'system' ? 'Automatically' : historyActorName(item));

function RecentActivity({ items, loading, error, onRetry }) {
    return (
        <section className={card} data-testid="overview-activity">
            <div className={cardHead}><span className="flex-1 text-[13.5px] font-semibold">Recent activity</span><span className="text-xs text-stone-500">Last 5 changes you can see</span></div>
            {loading && <p className="px-4 py-3 text-[13px] text-stone-500">Loading activity…</p>}
            {!loading && error && (
                <div className="flex items-center gap-2 px-4 py-3 text-[13px] text-red-700" role="alert">
                    Activity could not be loaded.<button type="button" onClick={onRetry} className="font-medium underline">Retry</button>
                </div>
            )}
            {!loading && !error && items.length === 0 && <p className="px-4 py-3 text-[13px] text-stone-500">No status changes or outreach yet.</p>}
            {!loading && !error && items.slice(0, 5).map((item, index) => {
                const actor = activityActor(item);
                const eventLevel = isEventLevel(item);
                return (
                    <div key={item.audit_event_id || item.id || index} className="flex min-w-0 items-center gap-2.5 border-b border-stone-100 px-4 py-2.5 text-[13px] last:border-b-0" data-testid="overview-activity-row" data-level={eventLevel ? 'event' : 'guest'}>
                        <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full border border-stone-200 bg-stone-100 text-[9.5px] font-semibold text-stone-700">{initials(actor)}</span>
                        <div className="flex min-w-0 flex-1 flex-col gap-px">
                            {eventLevel
                                ? <span className="truncate"><strong className="font-semibold">{actor}</strong> {eventLevelText(item)}</span>
                                : <span className="truncate">{actor} · <strong className="font-semibold">{item.contact_name || 'A contact'}</strong> · {activityLine(item)}</span>}
                            <span className="truncate text-xs tabular-nums text-stone-500">{[historySource(item) && humanize(historySourceLabel(item)), activityTime(item)].filter(Boolean).join(' · ')}</span>
                        </div>
                    </div>
                );
            })}
        </section>
    );
}

function OutreachTeam({ overview, onOpenTab }) {
    const after = overview.timeline.current !== 'before';
    const columns = after
        ? [['attended', 'ATTND'], ['meetings_booked', 'MTGS'], ['follow_ups_overdue', 'OVERDUE']]
        : [['on_list', 'LIST'], ['rsvp_confirmed', 'CONF'], ['follow_ups_overdue', 'OVERDUE']];
    const grid = { gridTemplateColumns: 'minmax(0,1fr) 52px 60px 64px' };
    const total = (key) => overview.team.reduce((sum, owner) => sum + metricValue(owner, key), 0);
    const value = (key, amount) => <span className={`text-right text-[13px] tabular-nums ${key === 'follow_ups_overdue' && amount > 0 ? 'font-semibold text-red-700' : ''}`}>{amount}</span>;
    return (
        <section className={card} data-testid="overview-team">
            <div className="flex flex-col gap-0.5 border-b border-stone-100 px-3.5 pb-2.5 pt-3">
                <span className="text-[13.5px] font-semibold">Outreach team</span>
                <span className="text-xs text-stone-500">Each Contact Owner on this event · click a name to filter Guests</span>
            </div>
            {overview.team.length === 0 ? <p className="px-3.5 py-3 text-[13px] text-stone-500">No contacts on this event yet.</p> : (
                <>
                    <div className="grid h-8 items-center border-b border-stone-100 bg-stone-50 px-3.5 text-[10.5px] font-semibold tracking-wider text-stone-500" style={grid}>
                        <span>OWNER</span>{columns.map(([key, label]) => <span key={key} className="text-right">{label}</span>)}
                    </div>
                    {overview.team.map((owner) => (
                        <button
                            key={owner.owner_id || 'unassigned'}
                            type="button"
                            disabled={!owner.owner_id}
                            onClick={() => onOpenTab('guests', { owner_id: owner.owner_id })}
                            className="grid min-h-[44px] w-full items-center border-b border-stone-100 px-3.5 text-left hover:bg-stone-50 disabled:cursor-default disabled:hover:bg-white"
                            style={grid}
                            data-testid={`overview-team-${owner.owner_id || 'unassigned'}`}
                        >
                            <span className="flex min-w-0 items-center gap-2">
                                <span className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full border border-stone-200 bg-stone-100 text-[9px] font-semibold text-stone-700">{initials(owner.name)}</span>
                                <span className="truncate text-[13px]">{owner.name}</span>
                            </span>
                            {columns.map(([key]) => <React.Fragment key={key}>{value(key, metricValue(owner, key))}</React.Fragment>)}
                        </button>
                    ))}
                    <div className="grid h-10 items-center bg-stone-50 px-3.5 text-[13px] font-semibold" style={grid}>
                        <span>Total</span>{columns.map(([key]) => <React.Fragment key={key}>{value(key, total(key))}</React.Fragment>)}
                    </div>
                </>
            )}
        </section>
    );
}

/** The event's photo counts (#900): the same server counters as the Photos tab and the Events list. */
function PhotosCard({ photos, onOpenTab }) {
    const counts = [
        ['photos_uploaded', 'Photos'], ['faces_detected', 'Faces detected'], ['faces_matched', 'Matched'],
        ['faces_to_review', 'To review'], ['photos_do_not_use', 'Do not use'],
    ];
    return (
        <section className={`${card} flex flex-col gap-2.5 px-3.5 pb-3.5 pt-3`} data-testid="overview-photos">
            <div className="flex items-center gap-2.5">
                <Camera className="h-4 w-4 text-stone-600" />
                <span className="flex-1 text-[13.5px] font-semibold">Photos</span>
                <Button variant="outline" size="sm" onClick={() => onOpenTab('photos')}>Open Photos</Button>
            </div>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[13px]">
                {counts.map(([key, label]) => (
                    <div key={key} className="flex items-baseline justify-between gap-2" data-testid={`overview-photos-${key}`}>
                        <dt className="text-stone-500">{label}</dt>
                        <dd className="font-semibold tabular-nums text-stone-900">{photos[key] ?? 0}</dd>
                    </div>
                ))}
            </dl>
        </section>
    );
}

function WorkflowCard({ workflow, overview, isAdmin, onChange }) {
    // #1013 (D1): the system milestones (rename only) first, then the follow-up stages an admin can edit. A stage
    // still in the old Before-the-event group belongs to a pre-#1013 snapshot and is not a column any more.
    const milestones = (workflow?.milestones || overview?.workflow_milestones || []).map((milestone) => ({ ...milestone, milestone: true }));
    const stages = (workflow?.stages || overview?.workflow_stages || [])
        .filter((stage) => (stage.group || stage.stage_group) !== 'before_event');
    const tone = (stage) => (stage.milestone ? TONE.stone : stage.terminal ? (/sales|convert/i.test(stage.key) ? TONE.emerald : TONE.stone) : TONE.blue);
    const chips = [...milestones, ...stages];
    return (
        <section className={`${card} flex flex-col gap-2.5 px-3.5 pb-3.5 pt-3`} data-testid="event-workflow-card">
            <div className="flex items-center gap-2.5">
                <span className="flex-1 text-[13.5px] font-semibold">Outreach workflow</span>
                {isAdmin && <Button variant="outline" size="sm" onClick={onChange} data-testid="workflow-change">Change</Button>}
            </div>
            <div className="flex items-center gap-2 text-[13px] font-semibold">
                <Workflow className="h-4 w-4 text-stone-600" />
                {workflow ? `${workflow.name || workflow.pipeline_name || 'Workflow'}${workflow.version ? ` · v${workflow.version}` : ''}` : 'Organization default'}
            </div>
            {chips.length > 0 && <div className="flex flex-wrap gap-1.5">{chips.map((stage) => <span key={stage.key} className={`inline-flex h-[22px] items-center rounded-md border px-2 text-[11.5px] font-medium ${tone(stage)}`}>{stage.label || stage.key}</span>)}</div>}
            {workflow?.binding_missing && <p className="text-xs text-amber-700">The selected workflow version is no longer available; the organization default is used.</p>}
            <span className="text-xs text-stone-500">{isAdmin ? 'Change opens the event’s workflow selector.' : 'Read-only. Only admins can change the workflow.'}</span>
        </section>
    );
}

function LinkedCampaigns({ event, canManage, getAuthHeader, onLink, onUnlink, navigate }) {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState(null);
    const [busy, setBusy] = useState(null);
    const linked = event.campaigns || [];
    const linkedIds = new Set(event.campaign_ids || linked.map((campaign) => campaign.campaign_id));
    const toggle = async () => {
        const next = !open;
        setOpen(next);
        if (next && options === null) {
            try {
                const res = await axios.get(`${API}/campaigns`, { headers: getAuthHeader() });
                setOptions(Array.isArray(res.data) ? res.data : (res.data?.data || []));
            } catch (err) {
                setOptions([]);
                toast.error(errorText(err, 'Campaigns could not be loaded'));
            }
        }
    };
    const eventTriggered = (options || []).filter((campaign) => !linkedIds.has(campaign.campaign_id)
        && (campaign.triggers || []).some((trigger) => String(trigger?.trigger_type || '').startsWith('event_')));
    const run = async (campaignId, action) => {
        setBusy(campaignId);
        try { await action(campaignId); setOpen(false); } finally { setBusy(null); }
    };
    return (
        <section className={card} data-testid="overview-campaigns">
            <div className={cardHead}>
                <span className="flex-1 text-[13.5px] font-semibold">Linked campaigns</span>
                {canManage && (
                    <div className="relative">
                        <Button variant="outline" size="sm" onClick={toggle} aria-expanded={open} data-testid="link-campaign"><Link2 className="mr-1 h-3.5 w-3.5" />Link campaign</Button>
                        {open && (
                            <div className="absolute right-0 top-9 z-40 flex w-[290px] max-w-[calc(100vw-48px)] flex-col rounded-[10px] border border-stone-200 bg-white p-1 shadow-xl" role="listbox" aria-label="Campaigns with event triggers">
                                <span className="px-2.5 pb-1 pt-2 text-[10.5px] font-semibold tracking-wider text-stone-500">CAMPAIGNS WITH EVENT TRIGGERS</span>
                                {options === null && <span className="px-2.5 py-2 text-[13px] text-stone-500">Loading…</span>}
                                {options !== null && eventTriggered.length === 0 && <span className="px-2.5 py-2 text-[13px] text-stone-500">No other campaign has an event trigger.</span>}
                                {eventTriggered.map((campaign) => (
                                    <button key={campaign.campaign_id} type="button" role="option" aria-selected="false" disabled={busy === campaign.campaign_id} onClick={() => run(campaign.campaign_id, onLink)} className="flex flex-col gap-px rounded-md px-2.5 py-2 text-left hover:bg-stone-50 disabled:opacity-60">
                                        <span className="text-[13px] font-medium">{campaign.name || 'Untitled campaign'}</span>
                                        <span className="text-xs text-stone-500">Trigger · {(campaign.triggers || []).filter((trigger) => String(trigger?.trigger_type || '').startsWith('event_')).map((trigger) => campaignTriggerLabel(trigger.trigger_type)).join(', ')}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {linked.length === 0 && <p className="px-3.5 py-3 text-[13px] text-stone-500">No campaigns linked. {canManage ? 'Link one so RSVP and attendance changes enroll contacts automatically.' : ''}</p>}
            {linked.map((campaign) => (
                <div key={campaign.campaign_id} className="flex items-start gap-2.5 border-b border-stone-100 px-3.5 py-2.5 last:border-b-0" data-testid={`linked-campaign-${campaign.campaign_id}`}>
                    <Send className="mt-0.5 h-3.5 w-3.5 flex-none text-stone-500" />
                    <button type="button" onClick={() => navigate(`/campaigns/${encodeURIComponent(campaign.campaign_id)}/execution`)} className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
                        <span className="truncate text-[13px] font-semibold hover:underline">{campaign.name}</span>
                        <span className="text-xs text-stone-600">Trigger · {campaign.trigger_summary}</span>
                    </button>
                    <div className="flex flex-col items-end gap-0.5">
                        <span className="text-[13px] font-semibold tabular-nums">{campaign.total_enrolled ?? 0}</span>
                        <span className="whitespace-nowrap text-[11.5px] text-stone-500">enrolled</span>
                    </div>
                    {canManage && (
                        <button type="button" title={`Unlink ${campaign.name}`} aria-label={`Unlink ${campaign.name}`} disabled={busy === campaign.campaign_id} onClick={() => run(campaign.campaign_id, onUnlink)} className="ml-1 flex h-6 w-6 items-center justify-center rounded text-stone-500 hover:bg-stone-100 hover:text-red-700 disabled:opacity-60">
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
            ))}
        </section>
    );
}

/** The registration-forms list from either wire shape: a bare array or `{data: [...]}`. */
const registrationFormsFrom = (payload) => (Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []));

/**
 * Which forms register people on this event (#919): `GET /api/events/{id}/registration-forms`, one read when the card
 * mounts. Live or Off follows `is_active`, NOT `status`: a form with status "draft" is still publicly live while it is
 * active, so "Draft" would mislead. The empty state's Go to Forms is hidden on a deleted (read-only) event.
 */
function RegistrationForms({ eventId, getAuthHeader, readOnly, navigate }) {
    const [load, setLoad] = useState({ status: 'loading', forms: [] });
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!eventId) return undefined;
        let cancelled = false;
        axios.get(`${API}/events/${encodeURIComponent(eventId)}/registration-forms`, { headers: getAuthHeader() })
            .then((res) => { if (!cancelled) setLoad({ status: 'ready', forms: registrationFormsFrom(res.data) }); })
            .catch(() => { if (!cancelled) setLoad({ status: 'error', forms: [] }); });
        return () => { cancelled = true; };
    }, [eventId, getAuthHeader, attempt]);

    const retry = () => { setLoad({ status: 'loading', forms: [] }); setAttempt((count) => count + 1); };
    const copyLink = async (form) => {
        try {
            await navigator.clipboard.writeText(publicFormUrl(form.slug));
            toast.success('Link copied');
        } catch {
            toast.error('The link could not be copied');
        }
    };

    if (!eventId) return null;
    return (
        <section className={card} aria-label="Registration forms" data-testid="overview-registration-forms">
            <div className={cardHead}>
                <span className="flex-1 text-[13.5px] font-semibold">Registration forms</span>
                {load.status === 'ready' && load.forms.length > 0 && <span className="text-xs tabular-nums text-stone-500">{load.forms.length}</span>}
            </div>
            {load.status === 'loading' && (
                <div className="px-3.5 py-3" aria-busy="true" data-testid="registration-forms-loading">
                    <div className="h-10 animate-pulse rounded-md bg-stone-100" />
                </div>
            )}
            {load.status === 'error' && (
                <div className="flex items-center gap-2 px-4 py-3 text-[13px] text-red-700" role="alert">
                    Registration forms could not be loaded.<button type="button" onClick={retry} className="font-medium underline">Retry</button>
                </div>
            )}
            {load.status === 'ready' && load.forms.length === 0 && (
                <div className="flex flex-col items-start gap-2 px-3.5 py-3 text-[13px] text-stone-500">
                    <p>No forms register people on this event. Open a form&apos;s Settings → Event registration to connect one.</p>
                    {!readOnly && <Button variant="outline" size="sm" onClick={() => navigate('/forms')}>Go to Forms</Button>}
                </div>
            )}
            {load.status === 'ready' && load.forms.length > 0 && (
                <ul className="m-0 list-none p-0">
                    {load.forms.map((form) => {
                        const name = form.name || 'Untitled form';
                        const live = Boolean(form.is_active);
                        return (
                            <li key={form.form_id} className="flex min-w-0 flex-col gap-2 border-b border-stone-100 px-3.5 py-2.5 last:border-b-0" data-testid={`registration-form-${form.form_id}`}>
                                <div className="flex min-w-0 items-center gap-2">
                                    <FileText className="h-3.5 w-3.5 flex-none text-stone-500" aria-hidden="true" />
                                    <span className="min-w-0 flex-1 truncate text-[13px] font-semibold" title={name}>{name}</span>
                                    <span className={`flex-none rounded-full px-2 py-0.5 text-[11px] font-medium ${live ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-stone-600'}`} data-testid="registration-form-state">{live ? 'Live' : 'Off'}</span>
                                    <span className="flex-none whitespace-nowrap text-[11.5px] tabular-nums text-stone-500">{form.registered ?? 0} registered</span>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    <Button variant="outline" size="sm" aria-label={`Open form ${name}`} onClick={() => navigate(`/forms/${encodeURIComponent(form.form_id)}/workflow`)}>Open form</Button>
                                    <Button variant="outline" size="sm" aria-label={`Copy link to ${name}`} onClick={() => copyLink(form)}>Copy link</Button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}

function OverviewTab({
    event, overview, loading, error, onRetry, history, onRetryHistory, onOpenTab, onEdit, onPublish, publishing,
    isAdmin, canManageCampaigns, getAuthHeader, onLinkCampaign, onUnlinkCampaign, navigate, readOnly,
}) {
    if (loading && !overview) {
        return (
            <div className="flex flex-col gap-4" data-testid="overview-loading" aria-busy="true">
                <div className="h-[150px] rounded-[10px] border border-stone-200 bg-stone-100" />
                <div className="flex flex-wrap gap-4">
                    <div className="flex min-w-0 flex-[1_1_560px] flex-col gap-4"><div className="h-[132px] rounded-[10px] border border-stone-200 bg-stone-100" /><div className="h-[220px] rounded-[10px] border border-stone-200 bg-stone-100" /></div>
                    <div className="flex min-w-[min(300px,100%)] flex-[0_1_340px] flex-col gap-4"><div className="h-[180px] rounded-[10px] border border-stone-200 bg-stone-100" /><div className="h-[120px] rounded-[10px] border border-stone-200 bg-stone-100" /></div>
                </div>
            </div>
        );
    }
    if (readOnly && event?.is_deleted && (error || !overview?.timeline)) {
        return (
            <div className="rounded-xl border border-stone-200 bg-white px-6 py-10 text-center text-[13px] text-stone-600" data-testid="overview-deleted">
                This event was deleted, so it has no live overview. Restore it to see its outreach again.
            </div>
        );
    }
    if (error || !overview?.timeline || !overview?.metrics || !Array.isArray(overview?.funnel)) {
        // Never fall back to cached `event.stats`: a stale number would contradict the Guests tab (#899).
        return (
            <div className="flex flex-col items-center gap-2.5 rounded-xl border border-red-200 bg-red-50/30 px-6 py-12 text-center" role="alert" data-testid="overview-error">
                <AlertTriangle className="h-5 w-5 text-red-700" />
                <p className="text-base font-semibold text-stone-900">{error?.denied ? 'You can’t view this overview' : 'Couldn’t load the overview'}</p>
                <p className="max-w-md text-[13px] text-stone-600">{error?.message || 'Something went wrong on our side. Nothing you saved has been lost.'}</p>
                {!error?.denied && <Button variant="outline" size="sm" onClick={onRetry}><RotateCcw className="mr-1.5 h-3.5 w-3.5" />Try again</Button>}
            </div>
        );
    }
    return (
        <div className="flex flex-col gap-4" data-testid="overview-tab">
            <section className={card}>
                <PhaseTimeline overview={overview} />
                <NextStep overview={overview} onOpenTab={onOpenTab} onPublish={onPublish} publishing={publishing} />
            </section>
            {overview.coverage && overview.coverage.complete === false && (
                <p className="text-xs text-amber-700" role="status">Some counts are partial: this event has more contacts than one read covers.</p>
            )}
            <div className="flex flex-wrap items-start gap-4">
                <div className="flex min-w-0 flex-[1_1_560px] flex-col gap-4">
                    <Funnel overview={overview} onOpenTab={onOpenTab} />
                    <EventDetailsCard event={event} overview={overview} onEdit={onEdit} readOnly={readOnly} />
                    {event.description && (
                        <section className={card}>
                            <div className={cardHead}><span className="flex-1 text-[13.5px] font-semibold">Description and agenda</span>{!readOnly && <button type="button" onClick={onEdit} className="text-[12.5px] font-medium underline underline-offset-2">Edit</button>}</div>
                            <p className="max-w-[720px] whitespace-pre-wrap px-4 pb-4 pt-3.5 text-[13px] leading-relaxed text-stone-700">{event.description}</p>
                        </section>
                    )}
                    <RecentActivity items={history.items} loading={history.loading} error={history.error} onRetry={onRetryHistory} />
                </div>
                <div className="flex min-w-[min(300px,100%)] flex-[0_1_340px] flex-col gap-4">
                    <OutreachTeam overview={overview} onOpenTab={onOpenTab} />
                    {overview.photos && <PhotosCard photos={overview.photos} onOpenTab={onOpenTab} />}
                    <WorkflowCard workflow={event.workflow} overview={overview} isAdmin={isAdmin} onChange={onEdit} />
                    <LinkedCampaigns event={event} canManage={canManageCampaigns && !readOnly} getAuthHeader={getAuthHeader} onLink={onLinkCampaign} onUnlink={onUnlinkCampaign} navigate={navigate} />
                    <RegistrationForms eventId={event.event_id} getAuthHeader={getAuthHeader} readOnly={readOnly} navigate={navigate} />
                </div>
            </div>
        </div>
    );
}
